/**
 * Browser half of the notification → screen mapping.
 *
 * Canonical copy: `backend/src/shared/notificationTargets.ts`. The API now
 * resolves the target server-side and ships it on every notification payload as
 * `target`, so this file's job is (a) to know which view keys the router in
 * `App.tsx` actually honours, and (b) to still work for rows created before the
 * `entityType` column existed. Keep the ENTITY_VIEW table below in step with the
 * backend copy.
 *
 * Before this, the backend emitted links like `/chats?id=123` and the router only
 * ever accepted a bare view key — so every notification click did nothing.
 */

/** Exactly the keys `App.tsx` accepts. Anything else navigates nowhere. */
export const ROUTER_VIEWS = [
  'dashboard',
  'chats',
  'requests',
  'warehouse',
  'ai',
  'integrations',
  'team',
  'billing',
  'profile',
  'settings',
  // Added with ApprovalsPage.tsx and LegalPage.tsx. Keep in step with
  // APP_VIEWS in the backend copy and with the allowlist in App.tsx.
  'approvals',
  'legal',
] as const;

export type RouterView = (typeof ROUTER_VIEWS)[number];

export type NotificationEntityType =
  | 'conversation'
  | 'deal'
  | 'product'
  | 'billing'
  | 'approval'
  | 'team'
  | 'settings';

/** Mirror of ENTITY_VIEW in the backend copy. */
export const ENTITY_VIEW: Record<NotificationEntityType, RouterView> = {
  conversation: 'chats',
  deal: 'requests',
  product: 'warehouse',
  billing: 'billing',
  // The dedicated approvals screen (pending requests + AI action audit trail).
  approval: 'approvals',
  team: 'team',
  settings: 'settings',
};

/** Legacy `link` prefixes that used to be written into the database. */
const LEGACY_HEAD_ENTITY: Record<string, NotificationEntityType> = {
  chats: 'conversation',
  chat: 'conversation',
  conversations: 'conversation',
  conversation: 'conversation',
  leads: 'conversation',
  deals: 'deal',
  deal: 'deal',
  requests: 'deal',
  orders: 'deal',
  inventory: 'product',
  warehouse: 'product',
  products: 'product',
  product: 'product',
  stock: 'product',
  billing: 'billing',
  pricing: 'billing',
  subscription: 'billing',
  payment: 'billing',
  approvals: 'approval',
  approval: 'approval',
  team: 'team',
  settings: 'settings',
};

export interface NotificationLike {
  entityType?: string | null;
  entityId?: number | null;
  link?: string | null;
  target?: { view?: string | null; entityId?: number | null } | null;
}

export interface ResolvedTarget {
  view: RouterView;
  entityType: NotificationEntityType | null;
  entityId: number | null;
}

export const NAVIGATE_EVENT = 'clientix:navigate';

export interface NavigateDetail {
  view: RouterView;
  entityType: NotificationEntityType | null;
  entityId: number | null;
}

export function isRouterView(value: unknown): value is RouterView {
  return typeof value === 'string' && (ROUTER_VIEWS as readonly string[]).includes(value);
}

function toEntityId(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function fromLegacyLink(link?: string | null): ResolvedTarget | null {
  if (!link || !link.trim()) return null;
  const [pathPart, queryPart = ''] = link.trim().replace(/^#/, '').split('?');
  const path = pathPart.replace(/^\/+|\/+$/g, '').toLowerCase();
  const segments = path.split('/');
  const head = segments[0] || '';
  const entityId =
    toEntityId(new URLSearchParams(queryPart).get('id')) ?? toEntityId(segments[1]);

  const entityType = LEGACY_HEAD_ENTITY[head];
  if (entityType) return { view: ENTITY_VIEW[entityType], entityType, entityId };
  if (isRouterView(head)) return { view: head, entityType: null, entityId };
  return null;
}

/**
 * Where this notification should open. Trusts the server-resolved `target` when
 * it names a view the router accepts, then the structured columns, then the
 * legacy link, and finally gives up on the dashboard instead of a dead click.
 */
export function notificationTarget(notification: NotificationLike): ResolvedTarget {
  const serverView = notification.target?.view;
  if (isRouterView(serverView)) {
    const entityType = (notification.entityType || null) as NotificationEntityType | null;
    return {
      view: serverView,
      entityType,
      entityId: toEntityId(notification.target?.entityId ?? notification.entityId),
    };
  }

  const entityType = notification.entityType as NotificationEntityType | undefined;
  if (entityType && ENTITY_VIEW[entityType]) {
    return {
      view: ENTITY_VIEW[entityType],
      entityType,
      entityId: toEntityId(notification.entityId),
    };
  }

  return fromLegacyLink(notification.link) ?? { view: 'dashboard', entityType: null, entityId: null };
}

/**
 * Open the screen a notification points at.
 *
 * Two channels on purpose:
 *  - the `clientix:navigate` event carries the entity id, which the hash cannot
 *    (the router allowlist only accepts bare view keys), and it also fires when
 *    the user is already on the target view — where a hash assignment produces no
 *    `hashchange` at all;
 *  - the hash is still set so the view survives a reload and stays in sync with
 *    the rest of the app.
 */
export function navigateToNotification(notification: NotificationLike): ResolvedTarget {
  const target = notificationTarget(notification);
  const detail: NavigateDetail = {
    view: target.view,
    entityType: target.entityType,
    entityId: target.entityId,
  };

  window.dispatchEvent(new CustomEvent<NavigateDetail>(NAVIGATE_EVENT, { detail }));
  if (window.location.hash.replace('#', '') !== target.view) {
    window.location.hash = target.view;
  }
  return target;
}
